import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';

import {
  OpTableColumn,
  OpTableRecordType,
  TableState,
} from 'new-components/DLS/OpTableCore/types';
import { intensiveApiEndpointNames } from 'utils/intensiveApiEndpointNames';
import { UseOpQueryOptions } from 'utils/customHooks/useOpQuery';
import { CsvExportButton } from 'new-components/DLS/OpTableCore/components';

import { GetterApiEndpointNames, RecordType } from '../types';

import { CursorDataFetchCsvExportButton } from './CursorDataFetchCsvExportButton';
import { DataFetchCsvExportButton } from './DataFetchCsvExportButton';

interface CsvExportButtonContainerProps<
  Endpoint extends GetterApiEndpointNames,
  Selection extends OpTableRecordType[] = Api.Response[Endpoint],
> {
  gtm?: string;
  opQueryProps: UseOpQueryOptions<Endpoint, Selection>;
  debouncedState: TableState<RecordType<Selection>>;
  filteredCount: number;
  selectedRows: RecordType<Selection>[];
  filename?: string;
  columnsFilterCallback?: (
    column: OpTableColumn<RecordType<Selection>>,
  ) => boolean;
}

/**
 * Container for DataFetchTable's CSV export button. Depending on the table and selected rows, it
 * will render a different component to best handle the data.
 */
export const CsvExportButtonContainer = <
  Endpoint extends GetterApiEndpointNames,
  Selection extends OpTableRecordType[] = Api.Response[Endpoint],
>({
  gtm,
  opQueryProps,
  debouncedState,
  filteredCount,
  selectedRows,
  filename,
  columnsFilterCallback,
}: CsvExportButtonContainerProps<Endpoint, Selection>) => {
  if (selectedRows.length > 0) {
    return (
      <CsvExportButton
        gtm={gtm}
        testId="csv-export-button"
        data={selectedRows.map((record) => {
          const newRecord = cloneDeep(record);

          debouncedState.columns.forEach((column) => {
            if (!column.key || column.hidden) return;
            const columnKey = String(column.key);

            const formattedValue = column.createCsvExportCellValue
              ? column.createCsvExportCellValue(record)
              : get(record, columnKey);

            set(newRecord, columnKey, formattedValue);
          });

          return newRecord;
        })}
        // Can filter columns with createColumns
        columns={debouncedState.columns.filter(
          columnsFilterCallback
            ? columnsFilterCallback || (() => true)
            : () => true,
        )}
        filename={filename}
      />
    );
  }

  if (intensiveApiEndpointNames.includes(opQueryProps.apiEndpointName)) {
    return (
      <CursorDataFetchCsvExportButton
        opQueryProps={opQueryProps}
        debouncedTableState={debouncedState}
        {...(filename && { filename })}
        {...(columnsFilterCallback && { columnsFilterCallback })}
      />
    );
  }

  return (
    <DataFetchCsvExportButton
      opQueryProps={opQueryProps}
      debouncedTableState={debouncedState}
      filteredCount={filteredCount}
      {...(columnsFilterCallback && { columnsFilterCallback })}
      {...(filename && { filename })}
    />
  );
};
